import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getUiErrorMessage, getUiErrorToastText } from '@/shared/api';
import {
  Button,
  ConfirmDialog,
  FormField,
  InlineAlert,
  Input,
  Modal,
  ModalActions,
  Textarea,
  toast,
} from '@/shared/ui';

import { useUpdateAdminOrderStatusMutation } from '../api/ordersApi';
import type { FulfilmentPatchTarget } from '../lib/orderFulfilmentStatusTransitions';
import {
  FULFILMENT_STATUS_NOTES_MAX,
  updateFulfilmentStatusNotesSchema,
  type UpdateFulfilmentStatusNotesValues,
} from '../schemas/updateFulfilmentStatusSchema';
import type { OrderStatus } from '../types';

const EMPTY_NOTES: UpdateFulfilmentStatusNotesValues = { notes: '' };

const DELIVERED_TYPED_VALUE = 'DELIVERED';

export interface OrderUpdateFulfilmentStatusDialogProps {
  open: boolean;
  orderId: string;
  orderNumber: string;
  currentStatus: OrderStatus;
  targetStatus: FulfilmentPatchTarget | null;
  onOpenChange: (open: boolean) => void;
}

export function OrderUpdateFulfilmentStatusDialog({
  open,
  orderId,
  orderNumber,
  currentStatus,
  targetStatus,
  onOpenChange,
}: OrderUpdateFulfilmentStatusDialogProps) {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deliveredTyped, setDeliveredTyped] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useUpdateAdminOrderStatusMutation();

  const form = useForm<UpdateFulfilmentStatusNotesValues>({
    resolver: zodResolver(updateFulfilmentStatusNotesSchema),
    defaultValues: EMPTY_NOTES,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (!open) {
      form.reset(EMPTY_NOTES);
      setConfirmOpen(false);
      setDeliveredTyped('');
      setSubmitError(null);
    }
  }, [open, form]);

  const close = () => onOpenChange(false);

  const isDelivered = targetStatus === 'DELIVERED';

  const onRequestConfirm = form.handleSubmit(() => {
    if (!targetStatus) return;
    if (isDelivered && deliveredTyped !== DELIVERED_TYPED_VALUE) {
      setSubmitError(t('fulfilmentStatus.errors.deliveredTypeRequired'));
      return;
    }
    setSubmitError(null);
    setConfirmOpen(true);
  });

  const onConfirm = async () => {
    if (!targetStatus) return;
    const notesRaw = form.getValues('notes')?.trim() ?? '';
    setSubmitError(null);
    try {
      const result = await mutation.mutateAsync({
        orderId,
        body: {
          status: targetStatus,
          ...(notesRaw.length > 0 ? { notes: notesRaw } : {}),
        },
      });
      toast.success({
        title: result.message || t('fulfilmentStatus.toasts.successTitle'),
        description: t('fulfilmentStatus.toasts.successDescription', { orderNumber }),
      });
      setConfirmOpen(false);
      close();
    } catch (err) {
      setConfirmOpen(false);
      const ui = getUiErrorToastText(tc, err);
      const message = getUiErrorMessage(tc, err);
      const isTransitionError = /cannot transition/i.test(message);
      setSubmitError(
        isTransitionError ? `${message} ${t('fulfilmentStatus.errors.transitionHint')}` : message,
      );
      toast.error({
        title: ui.title,
        description: isTransitionError
          ? `${ui.description ?? message} ${t('fulfilmentStatus.errors.transitionHint')}`
          : ui.description,
      });
    }
  };

  if (!targetStatus) return null;

  const notesError = form.formState.errors.notes
    ? t('fulfilmentStatus.errors.notesTooLong', { max: FULFILMENT_STATUS_NOTES_MAX })
    : undefined;

  return (
    <>
      <Modal
        open={open && !confirmOpen}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={t('fulfilmentStatus.modal.title')}
        description={t('fulfilmentStatus.modal.description', {
          orderNumber,
          from: t(`status.${currentStatus}`),
          to: t(`status.${targetStatus}`),
        })}
        showClose
        size="md"
        footer={
          <ModalActions>
            <Button type="button" variant="secondary" onClick={close} disabled={mutation.isPending}>
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              form="order-fulfilment-status-form"
              variant="primary"
              disabled={mutation.isPending}
            >
              {t('fulfilmentStatus.modal.continue')}
            </Button>
          </ModalActions>
        }
      >
        <form
          id="order-fulfilment-status-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => void onRequestConfirm(e)}
        >
          {isDelivered ? (
            <InlineAlert className="border-warning-subtle bg-warning-subtle/30 text-warning-strong">
              {t('fulfilmentStatus.modal.deliveredWarning')}
            </InlineAlert>
          ) : null}

          <FormField error={notesError}>
            <FormField.Label>{t('fulfilmentStatus.modal.notesLabel')}</FormField.Label>
            <FormField.Control>
              <Textarea
                rows={3}
                maxLength={FULFILMENT_STATUS_NOTES_MAX}
                value={form.watch('notes') ?? ''}
                onChange={(e) => form.setValue('notes', e.target.value, { shouldValidate: true })}
                placeholder={t('fulfilmentStatus.modal.notesPlaceholder')}
              />
            </FormField.Control>
            <FormField.Description>
              {t('fulfilmentStatus.modal.notesHint', { max: FULFILMENT_STATUS_NOTES_MAX })}
            </FormField.Description>
          </FormField>

          {isDelivered ? (
            <FormField
              error={
                submitError === t('fulfilmentStatus.errors.deliveredTypeRequired')
                  ? submitError
                  : undefined
              }
              required
            >
              <FormField.Label required>{t('fulfilmentStatus.modal.deliveredTypeLabel')}</FormField.Label>
              <FormField.Control>
                <Input
                  autoComplete="off"
                  className="uppercase"
                  value={deliveredTyped}
                  onChange={(e) => {
                    setDeliveredTyped(e.target.value.toUpperCase());
                    if (submitError) setSubmitError(null);
                  }}
                  placeholder={DELIVERED_TYPED_VALUE}
                />
              </FormField.Control>
              <FormField.Description>{t('fulfilmentStatus.modal.deliveredTypeHint')}</FormField.Description>
            </FormField>
          ) : null}

          {submitError && !isDelivered ? <InlineAlert>{submitError}</InlineAlert> : null}
          {submitError && isDelivered && submitError !== t('fulfilmentStatus.errors.deliveredTypeRequired') ? (
            <InlineAlert>{submitError}</InlineAlert>
          ) : null}
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          isDelivered
            ? t('fulfilmentStatus.confirm.deliveredTitle')
            : t('fulfilmentStatus.confirm.title')
        }
        description={t('fulfilmentStatus.confirm.description', {
          orderNumber,
          status: t(`status.${targetStatus}`),
        })}
        confirmLabel={t('fulfilmentStatus.confirm.confirm')}
        confirmVariant={isDelivered ? 'primary' : 'primary'}
        isConfirming={mutation.isPending}
        onConfirm={() => void onConfirm()}
      />
    </>
  );
}
